import React, { useState, useEffect } from 'react'
import { legalService, AcceptanceStatus } from '../../services/legalService'
import { useAuth } from '../../context/AuthContext'
import styles from './TermsUpdateModal.module.css'

export default function TermsUpdateModal() {
  const { user, organization, signOut } = useAuth()
  const [acceptanceStatus, setAcceptanceStatus] = useState<AcceptanceStatus[]>([])
  const [showModal, setShowModal] = useState(false)
  const [loading, setLoading] = useState(true)
  const [accepting, setAccepting] = useState(false)
  const [accepted, setAccepted] = useState<Record<string, boolean>>({})
  const [error, setError] = useState('')

  useEffect(() => {
    if (!user) return

    const checkAcceptance = async () => {
      try {
        const status = await legalService.checkUserAcceptanceStatus(user.id)
        const needsAcceptance = status.some(s => !s.has_accepted)

        setAcceptanceStatus(status)
        setShowModal(needsAcceptance)
        setLoading(false)
      } catch (err) {
        console.error('Error checking acceptance status:', err)
        setLoading(false)
      }
    }

    checkAcceptance()
  }, [user])

  const handleAccept = async () => {
    if (!user) return

    const unaccepted = acceptanceStatus.filter(s => !s.has_accepted)
    const allChecked = unaccepted.every(s => accepted[s.document_type])

    if (!allChecked) {
      setError('You must accept all updated legal documents to continue.')
      return
    }

    setAccepting(true)
    setError('')

    try {
      const docsToAccept = unaccepted.map(s => ({
        type: s.document_type,
        version: s.latest_version
      }))

      console.log('[TermsUpdateModal] Recording acceptances for:', docsToAccept)

      await legalService.recordMultipleAcceptances(
        user.id,
        organization?.id || null,
        docsToAccept,
        'update'
      )

      console.log('[TermsUpdateModal] Acceptances recorded successfully')

      const refreshedStatus = await legalService.checkUserAcceptanceStatus(user.id)
      const allAccepted = refreshedStatus.every(s => s.has_accepted)

      setAcceptanceStatus(refreshedStatus)
      if (allAccepted) {
        setShowModal(false)
      } else {
        setError('Some documents could not be confirmed as accepted. Please try again.')
      }
      setAccepting(false)
    } catch (err: any) {
      console.error('[TermsUpdateModal] Error recording acceptances:', err)
      setError(err.message || 'Failed to record acceptance. Please try again.')
      setAccepting(false)
    }
  }

  const handleDecline = async () => {
    await signOut()
  }

  if (loading || !showModal) return null

  const unaccepted = acceptanceStatus.filter(s => !s.has_accepted)

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 className={styles.title}>Updated Legal Terms</h2>
          <p className={styles.subtitle}>
            We've updated our legal documents. Please review and accept the changes to continue using AllocatIQ.
          </p>
        </div>

        <div className={styles.content}>
          {unaccepted.map(status => (
            <div key={status.document_type} className={styles.docSection}>
              <label className={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={accepted[status.document_type] || false}
                  onChange={e => setAccepted({ ...accepted, [status.document_type]: e.target.checked })}
                />
                <span className={styles.checkboxLabel}>
                  I have reviewed and accept the updated{' '}
                  <a
                    href={`/${status.document_type}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.link}
                  >
                    {getDocumentTitle(status.document_type)}
                  </a>
                  <span className={styles.version}>
                    (v{status.latest_version})
                  </span>
                </span>
              </label>

              <div className={styles.changes}>
                <strong>What changed:</strong> Updated to version {status.latest_version}
                {status.accepted_version && ` from ${status.accepted_version}`}
              </div>
            </div>
          ))}

          {error && <div className={styles.error}>{error}</div>}
        </div>

        <div className={styles.footer}>
          <button
            onClick={handleDecline}
            className={styles.btnSecondary}
            disabled={accepting}
          >
            Sign out
          </button>
          <button
            onClick={handleAccept}
            className={styles.btnPrimary}
            disabled={accepting}
          >
            {accepting ? 'Accepting...' : 'Accept and continue'}
          </button>
        </div>

        <div className={styles.notice}>
          If you decline these terms, you will be signed out and unable to use AllocatIQ until you accept.
        </div>
      </div>
    </div>
  )
}

function getDocumentTitle(type: string): string {
  const titles: Record<string, string> = {
    terms: 'Terms of Service',
    privacy: 'Privacy Policy',
    sla: 'Service Level Agreement',
    aup: 'Acceptable Use Policy',
    dpa: 'Data Processing Agreement'
  }
  return titles[type] || type
}
